import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { Bug, RefreshCw } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { isAdminUser } from '../helpers/accessControl'
import { schedulerService } from '../services/schedulerService'

const eventLabels = {
  gallery_refresh_scheduled: 'Naplánována kontrola galerie',
  gallery_initial_complete: 'Počáteční galerie je kompletní',
  gallery_refresh_expanded: 'Galerie se rozšířila',
  gallery_refresh_unchanged: 'Galerie zůstala beze změny',
  gallery_refresh_complete: 'Galerie je kompletní',
  gallery_refresh_exhausted: 'Ukončeny automatické kontroly',
  gallery_refresh_failed: 'Kontrola galerie selhala',
  gallery_image_failed: 'Stažení obrázku selhalo',
  gallery_refresh_manual_requested: 'Ručně vyžádána kontrola',
  gallery_lifecycle_migrated: 'Galerie převedena do nového režimu',
  gallery_refresh_reactivated: 'Kontrola galerie obnovena po návratu nabídky',
  detail_refreshed: 'Detail nabídky aktualizován',
  detail_not_found: 'Nabídka už na SReality není dostupná',
}

const stateLabels = {
  scheduled: 'čeká na další kontrolu',
  complete: 'kompletní',
  stable_small_gallery: 'stabilní malá galerie',
  paused_listing_removed: 'pozastaveno — nabídka byla odstraněna',
}

const PropertyScrapeDebug = ({ property, onRequested }) => {
  const { user } = useAuth()
  const admin = isAdminUser(user)
  const [debug, setDebug] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [message, setMessage] = useState('')

  const loadDebug = useCallback(async () => {
    if (!admin || !property?.propertyId) return
    setLoading(true)
    try {
      setDebug(await schedulerService.propertyDebug(property.propertyId))
      setError('')
    } catch (requestError) {
      setError(requestError.message || 'Scraper debug se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }, [admin, property?.propertyId])

  useEffect(() => {
    loadDebug()
  }, [loadDebug])

  if (!admin) return null

  const refresh = debug?.imageRefresh
  const events = [...(debug?.scrapeEvents || [])].reverse()

  const retry = async () => {
    setRequesting(true)
    setMessage('')
    try {
      await schedulerService.retryGallery(property.propertyId)
      setMessage('Kontrola galerie byla zařazena do fronty.')
      await loadDebug()
      onRequested?.()
    } catch (error) {
      setMessage(error.message || 'Kontrolu se nepodařilo zařadit.')
    } finally {
      setRequesting(false)
    }
  }

  return (
    <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Bug className="h-4 w-4 text-violet-700" /><h3 className="font-semibold text-slate-950">Scraper debug</h3></div>
          <p className="mt-1 text-xs text-slate-500">{debug?.ownerLane || 'legacy'} · {debug?.canonicalScopeKey || 'bez canonical scope'}</p>
        </div>
        <button type="button" disabled={requesting} onClick={retry} className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 px-2.5 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50"><RefreshCw className={`h-3.5 w-3.5 ${requesting ? 'animate-spin' : ''}`} />Zkontrolovat galerii</button>
      </div>

      {loading && !debug && <div className="mt-4 text-xs text-slate-500">Načítám scraper debug…</div>}
      {error && <div className="mt-4 text-xs text-red-700">{error}</div>}

      {debug && <>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-slate-50 p-2"><div className="text-slate-500">Stav</div><div className="mt-1 font-medium">{stateLabels[refresh?.state] || refresh?.state || 'bez dat'}</div></div>
        <div className="rounded-md bg-slate-50 p-2"><div className="text-slate-500">Obrázky</div><div className="mt-1 font-medium">{refresh?.mirroredCount ?? property.gallery?.length ?? 0} / {refresh?.sourceCount ?? '—'}</div></div>
        <div className="rounded-md bg-slate-50 p-2"><div className="text-slate-500">Kontroly</div><div className="mt-1 font-medium">{refresh?.checkNumber ?? 0} / 3</div></div>
        <div className="rounded-md bg-slate-50 p-2"><div className="text-slate-500">Další kontrola</div><div className="mt-1 font-medium">{refresh?.nextCheckAt ? moment(refresh.nextCheckAt).format('DD.MM.YYYY HH:mm') : '—'}</div></div>
      </div>
      {message && <div className="mt-3 text-xs text-violet-700">{message}</div>}

      <details className="mt-4">
        <summary className="cursor-pointer text-xs font-medium text-slate-700">Události scraperu ({events.length})</summary>
        <div className="mt-3 max-h-72 space-y-2 overflow-auto">
          {events.map((event, index) => (
            <div key={`${event.at || 'event'}-${index}`} className="rounded-md border border-slate-100 p-2 text-xs">
              <div className="flex justify-between gap-2"><span className="font-medium">{eventLabels[event.type] || event.type}</span><span className="shrink-0 text-slate-400">{event.at ? moment(event.at).format('DD.MM. HH:mm') : '—'}</span></div>
              {(event.sourceCount !== undefined || event.mirroredCount !== undefined) && <div className="mt-1 text-slate-500">Zdroj: {event.sourceCount ?? '—'} · Uloženo: {event.mirroredCount ?? '—'}</div>}
              {event.error && <div className="mt-1 text-red-700">{event.error}</div>}
            </div>
          ))}
          {!events.length && <div className="text-slate-400">Zatím nejsou zaznamenané žádné události.</div>}
        </div>
      </details>
      </>}
    </section>
  )
}

export default PropertyScrapeDebug
